import { useActionDispatcher, useApi } from "@judo/actions";
import {
	DispatchProvider,
	useApplication,
	useDataStore,
	useNavigation,
	usePageContext,
	useResolvedPageActionOverrides,
} from "@judo/core";
import { useNotifications } from "@judo/feedback";
import type { ClassType, PageContainer, PageDefinition, RelationType, Table } from "@judo/model-api";
import { Box } from "@mui/material";
import { useCallback, useMemo } from "react";
import { PageHeader } from "../renderers/PageHeader";
import { TableRenderer } from "../renderers/TableRenderer";
import { buildTableMask } from "../utils/build-page-mask";
import { findOnInitAction } from "../utils/find-on-init-action";
import { findVisualElementByType } from "../utils/find-visual-element";

export interface TableContainerRendererProps {
	page: PageDefinition;
}

/**
 * Renderer for TABLE type containers.
 * Fetches table data on mount via onInit action (Refresh).
 */
export function TableContainerRenderer({ page }: TableContainerRendererProps) {
	// Container is already resolved in page
	const container = page.container as PageContainer;

	// Get page context to access transfer ID
	const pageContext = usePageContext();

	// Get required contexts for action dispatcher
	const navigation = useNavigation();
	const dataStore = useDataStore();
	const application = useApplication();
	const api = useApi();
	const notifications = useNotifications();

	// Find Table visual element in container children
	const tableElement = findVisualElementByType<Table>(container.children, "Table");

	// Get rowsPerPage (defaults to 10 if not specified or invalid)
	const rowsPerPage = tableElement?.rowsPerPage && tableElement.rowsPerPage > 0 ? tableElement.rowsPerPage : 10;

	// Get resolved page action overrides from customizations
	const actionOverrides = useResolvedPageActionOverrides();

	// Find the onInit action and ensure targetType is set on it.
	// The return value is not used directly — the mutation is the important side effect,
	// since useTableData finds the same action object via page.actions.
	// oxlint-disable-next-line no-unused-vars
	const _onInitAction = useMemo(() => {
		const action = findOnInitAction(page, container);

		// If action exists and has a RefreshActionDefinition, ensure targetType is set
		if (action && action.actionDefinition["@type"] === "RefreshActionDefinition") {
			const existingTargetType = action.actionDefinition.targetType;

			// If no targetType is set, derive it from the dataElement
			if (!existingTargetType) {
				// Prefer page.dataElement, fallback to container.dataElement
				const dataElement = page.dataElement || container.dataElement;

				if (dataElement) {
					// For RelationType (relation tables), use the target ClassType
					// For ClassType, use it directly
					let targetType: ClassType | undefined;

					if (dataElement["@type"] === "RelationType") {
						const relation = dataElement as RelationType;
						targetType = relation.target;
					} else {
						targetType = dataElement as ClassType;
					}

					// Mutate the action definition to add targetType (parser should do this, but doesn't)
					if (targetType) {
						action.actionDefinition.targetType = targetType;
					} else {
						console.error(
							"[TableContainerRenderer] Failed to set valid targetType - could not derive from dataElement"
						);
					}
				}
			}
		}

		return action;
	}, [page, container]);

	// Detect selector pages (opened via Add/Set selector actions)
	const isSelector = String(page.isSelector) === "true";
	const ownerTransfer = pageContext?.params?.ownerTransfer as Record<string, unknown> | undefined;

	// Detect operation input selector pages (opened via OpenOperationInputSelectorAction)
	const isOperationInput = pageContext?.params?.isOperationInput === true;

	// Get transfer from page context for relation tables
	// Relation tables need the parent transfer's signedIdentifier to know which relations to load
	const transfer = useMemo(() => {
		const transferId = pageContext?.transferId;
		const signedIdentifier = pageContext?.params?.signedIdentifier as string | undefined;

		if (!transferId || !signedIdentifier) return undefined;

		return {
			__identifier: transferId,
			__signedIdentifier: signedIdentifier,
		};
	}, [pageContext?.transferId, pageContext?.params]);

	// Memoize dispatcher context - include pageTransferId and rowsPerPage in additionalContext
	const dispatcherContext = useMemo(
		() =>
			({
				navigation,
				data: dataStore,
				registry: application,
				api,
				transfer,
				notifications,
				actionOverrides,
				additionalContext: {
					pageTransferId: pageContext.transferId,
					rowsPerPage,
					// Pass selector context so RefreshActionHandler can call getRelationRange / getOperationInputRange
					...(isSelector
						? {
								isSelector: true,
								ownerTransfer,
								...(isOperationInput ? { isOperationInput: true } : {}),
							}
						: {}),
				},
			}) as unknown as Parameters<typeof useActionDispatcher>[0],
		[
			navigation,
			dataStore,
			application,
			api,
			transfer,
			notifications,
			pageContext.transferId,
			rowsPerPage,
			actionOverrides,
			isSelector,
			ownerTransfer,
			isOperationInput,
		]
	);

	// Initialize action dispatcher
	const { dispatch } = useActionDispatcher(dispatcherContext);

	// Compute the table mask from the table element and container.
	// This mask is used to enrich all RefreshActionDefinition dispatches so that
	// the _mask is always included regardless of how the refresh is triggered.
	const tableMask = useMemo(
		() => (tableElement ? buildTableMask(tableElement, container) : undefined),
		[tableElement, container]
	);

	// Wrap dispatch so that ALL RefreshActionDefinition dispatches automatically include
	// the table mask. This guarantees _mask is sent regardless of call-site (PageHeader
	// button click, programmatic dispatch, downstream DispatchProvider consumers, etc.).
	const enrichedDispatch = useCallback(
		async (action: any) => {
			if (action.actionDefinition?.["@type"] === "RefreshActionDefinition" && tableMask) {
				return dispatch({
					...action,
					queryCustomizer: {
						...action.queryCustomizer,
						_mask: tableMask,
					},
				});
			}
			return dispatch(action);
		},
		[dispatch, tableMask]
	);

	// NOTE: Initial data loading is handled by useTableData (inside TableRenderer).
	// useTableData dispatches the RefreshAction on mount with the current sort/filter/pagination
	// state, _mask, and countRecords. This avoids duplicate requests and ensures sort state
	// is always included in the initial load.

	if (!tableElement) {
		return (
			<Box p={2} data-testid="table-container::no-table">
				No table element found in container
			</Box>
		);
	}

	// Don't render PageHeader in dialogs - dialog handles its own title and actions
	const showPageHeader = !pageContext?.isDialog;

	return (
		<DispatchProvider dispatch={enrichedDispatch}>
			<Box
				data-testid={`table-container::${container.name ?? container["xmi:id"]}`}
				sx={{ animation: "judoFadeInUp 0.4s cubic-bezier(0.4,0,0.2,1) both" }}
			>
				{showPageHeader && <PageHeader page={page} onDispatch={enrichedDispatch} />}
				<TableRenderer element={tableElement} page={page} />
			</Box>
		</DispatchProvider>
	);
}

export default TableContainerRenderer;
